import { useState } from 'react'

import { boatStep } from '../../lib/physics'
import { Predict, useFrames } from './common'

/**
 * Лодочка на слюде (урок 1): горячая металлическая лодочка на листке слюды сама раскачивается.
 * Слюда плохо проводит тепло: под лодочкой её верх нагревается, расширяется и вздувается
 * бугорком, лодочка скатывается, по инерции проскакивает — и делает себе новый бугорок.
 * Пока горит горючее, колебания не гаснут; остыла — успокоилась.
 */

const PX = 34 // px на единицу смещения
const ЦЕНТР = 180
const ВЕРХ_СЛЮДЫ = 150

export default function BoatLab() {
  const [горит, setГорит] = useState(false)
  const [s, setS] = useState({ x: 0.04, v: 0, жар: 0, бугор: 0 })

  useFrames((dt) => {
    setS((p) => {
      // жар растёт, пока горит горючее, и медленно спадает после
      const жар = Math.max(0, Math.min(1, p.жар + (горит ? dt / 2 : -dt / 8)))
      let { x, v } = p
      const n = 8
      for (let i = 0; i < n; i++) [x, v] = boatStep(x, v, жар, dt / n)
      // бугорок слюды «догоняет» лодочку с запаздыванием — нагрев не мгновенный
      const бугор = p.бугор + (x - p.бугор) * Math.min(1, dt * 3)
      return { x, v, жар, бугор }
    })
  })

  const bx = ЦЕНТР + s.x * PX
  const высота = 10 * s.жар // насколько вздулась слюда
  const слюда: string[] = []
  for (let px = 40; px <= 320; px += 4) {
    const d = (px - (ЦЕНТР + s.бугор * PX)) / 26
    слюда.push(`${px},${ВЕРХ_СЛЮДЫ - высота * Math.exp(-d * d)}`)
  }
  const подЛодкой = ВЕРХ_СЛЮДЫ - высота * Math.exp(-(((bx - (ЦЕНТР + s.бугор * PX)) / 26) ** 2))
  const наклон = (-(bx - (ЦЕНТР + s.бугор * PX)) * высота) / 40
  const колеблется = Math.abs(s.x) > 0.3 || Math.abs(s.v) > 1

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 230" role="img" aria-label="Лодочка на слюде">
        <rect x={30} y={ВЕРХ_СЛЮДЫ + 4} width={300} height={34} rx={3} className="fill-metal ink" strokeWidth={1} />
        <text x={330} y={ВЕРХ_СЛЮДЫ + 52} textAnchor="end" className="lab-tick">алюминий — холодный</text>
        <polyline points={слюда.join(' ')} className="ink-band" strokeWidth={4} fill="none" />
        <text x={40} y={ВЕРХ_СЛЮДЫ - 8} className="lab-tick">слюда</text>
        <g transform={`translate(${bx} ${подЛодкой}) rotate(${наклон})`}>
          <path d="M-30 -2 Q0 12 30 -2 L24 -16 L-24 -16 Z" className="fill-boat ink" strokeWidth={1.5} />
          {горит && (
            <path
              d={`M-6 -16 Q-8 ${-30 - 4 * Math.sin(Date.now() / 90)} 0 ${-40 - 3 * Math.cos(Date.now() / 70)} Q8 -30 6 -16 Z`}
              className="fill-flame"
            />
          )}
        </g>
        <rect x={20} y={18} width={10} height={60} rx={4} className="fill-cabin ink" strokeWidth={1} />
        <rect x={21} y={18 + 60 * (1 - s.жар)} width={8} height={60 * s.жар} rx={3} className="fill-heavy" />
        <text x={36} y={28} className="lab-tick">жар</text>
      </svg>

      <div className="lab-caption">
        {горит
          ? колеблется
            ? 'Лодочка раскачивается сама: под ней слюда нагревается сверху, расширяется и вздувается — лодочка скатывается с бугорка.'
            : 'Лодочка нагревается… слюда под ней начинает вздуваться.'
          : s.жар > 0.05
            ? 'Горючее потушено: лодочка остывает, раскачивание затухает.'
            : 'Лодочка холодная и стоит спокойно. Подожги горючее.'}
      </div>

      <Predict
        question="Почему лодочка раскачивается, хотя её никто не толкает?"
        options={['её толкает пламя', 'под ней вздувается нагретая слюда', 'её притягивает алюминий']}
        right={1}
        revealed={колеблется}
        explain="Слюда плохо проводит тепло: верхний слой нагревается и расширяется, нижний остаётся холодным — лист изгибается бугорком. Лодочка скатывается, по инерции проскакивает и греет новое место. Тепло превращается в движение — так работает тепловой двигатель."
      />

      <div className="row-wrap" style={{ marginTop: 12 }}>
        {горит ? (
          <button className="btn" onClick={() => setГорит(false)}>💨 Потушить</button>
        ) : (
          <button className="btn btn-primary" onClick={() => setГорит(true)}>🔥 Поджечь горючее</button>
        )}
      </div>
      <p className="muted small">
        Одно явление природы — несколько физических: тепловые (нагревание, расширение) и
        механическое (колебания).
      </p>
    </div>
  )
}
